// Browser-resident git repo as the canonical storage layer.
//
// The Accelerando pattern, ported to a single-user browser app from
// agicore-foundry (same shape, same primitives, different repo name).
// Phase 1 lands just the substrate — no consumer wired yet. Channels,
// the document library, templates, and per-user prefs all still live
// in localStorage / zustand stores. Phase 2 dual-writes them to git
// on every mutation; Phase 3 adds the GitHub auto-push for off-device
// durability.
//
// Storage backend is Lightning-FS over IndexedDB — same browser site-
// data bucket localStorage already lives in, so day-one durability
// hasn't changed yet. The win lands when Phase 3 ships GitHub backup
// and you can blow away your browser cache without losing the
// brainstorm you ran yesterday.
//
// All mutations route through a serial promise queue so concurrent
// writes (chat composer + brainstorm AI stream + library upload firing
// in parallel) don't race each other's `add → commit` pairs.

import FS from "@isomorphic-git/lightning-fs";
import git from "isomorphic-git";

const FS_NAME = "playground-git";
const REPO_DIR = "/playground";
const DEFAULT_BRANCH = "main";
const DEFAULT_AUTHOR = {
  name: "BabyAI Playground",
  email: "noreply@babyai-playground.local",
};

export interface GitAuthor {
  name: string;
  email: string;
}

export interface GitCommitMeta {
  oid: string;
  message: string;
  author: GitAuthor;
  timestamp: number;
}

let fsInstance: FS | null = null;
let initPromise: Promise<void> | null = null;
let author: GitAuthor = { ...DEFAULT_AUTHOR };

function getFs(): FS {
  if (!fsInstance) fsInstance = new FS(FS_NAME);
  return fsInstance;
}

/** Lazy one-time repo init. Idempotent. */
export function initGitStore(): Promise<void> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    const fs = getFs();
    try {
      await fs.promises.mkdir(REPO_DIR);
    } catch (e) {
      // EEXIST is the happy path — repo dir already created.
      if (!isEexist(e)) throw e;
    }
    // `git init` is idempotent on an existing repo (it just resets HEAD
    // to the default branch). Safe to call on every app boot.
    await git.init({ fs, dir: REPO_DIR, defaultBranch: DEFAULT_BRANCH });
  })();
  return initPromise;
}

/** Override the commit identity. Call once after Google sign-in
 *  lands the user profile; otherwise commits are attributed to the
 *  default app-level identity. */
export function setGitAuthor(next: GitAuthor): void {
  author = { name: next.name || DEFAULT_AUTHOR.name, email: next.email || DEFAULT_AUTHOR.email };
}

// ─── Serial mutation queue ──────────────────────────────────────────────────
//
// isomorphic-git's add/commit pair is not atomic on its own. Two writes
// landing on the same tick can interleave into a single commit (best
// case) or a corrupted index entry (worst case). Funnel every mutation
// through a single-slot queue so the order of operations matches the
// order the consumer modules fired them.

let tail: Promise<unknown> = Promise.resolve();
function enqueue<T>(fn: () => Promise<T>): Promise<T> {
  const next = tail.then(fn, fn);
  // Swallow rejections on the chain itself so one failed write doesn't
  // poison every subsequent enqueue. Callers still get the rejection
  // via the returned promise.
  tail = next.catch(() => undefined);
  return next;
}

// ─── Mutations ──────────────────────────────────────────────────────────────

/** Write a file to the working tree and stage it. Returns true when
 *  the on-disk bytes changed (so the caller can skip a no-op commit).
 *  Creates intermediate directories. */
export async function writeFile(path: string, content: string): Promise<boolean> {
  await initGitStore();
  return enqueue(async () => {
    const fs = getFs();
    const abs = absPath(path);
    await mkdirP(absPath(dirname(path)));
    let changed = true;
    try {
      const prev = (await fs.promises.readFile(abs, "utf8")) as string;
      if (prev === content) changed = false;
    } catch {
      // No prior file is fine — first write counts as a change.
    }
    if (changed) {
      await fs.promises.writeFile(abs, content, "utf8");
      await git.add({ fs, dir: REPO_DIR, filepath: path });
    }
    return changed;
  });
}

/** Remove a file from the working tree and stage the deletion. No-op
 *  if the file is already gone. */
export async function deleteFile(path: string): Promise<boolean> {
  await initGitStore();
  return enqueue(async () => {
    const fs = getFs();
    const abs = absPath(path);
    try {
      await fs.promises.unlink(abs);
    } catch (e) {
      if (isEnoent(e)) return false;
      throw e;
    }
    await git.remove({ fs, dir: REPO_DIR, filepath: path });
    return true;
  });
}

/** Commit whatever's currently staged. Returns the new commit oid, or
 *  null when the working tree was clean (nothing to record). */
export async function commit(message: string): Promise<string | null> {
  await initGitStore();
  const oid = await enqueue(async () => {
    const fs = getFs();
    // Skip when there's literally nothing to commit; isomorphic-git
    // happily writes an empty commit otherwise, which would clutter
    // the history with placeholder revisions on idle ticks.
    if (!(await hasStagedChanges(fs))) return null;
    return git.commit({
      fs,
      dir: REPO_DIR,
      message,
      author,
    });
  });
  if (oid !== null) emitCommit(oid, message);
  return oid;
}

// ─── Commit event bus ───────────────────────────────────────────────────────
//
// Lets higher layers (auto-push, future UI badges) react to local
// commits without git-store taking a dependency on them. Subscribers
// are called synchronously after a successful commit; throws in a
// listener are caught and logged so one bad subscriber can't break
// the others.

export type CommitListener = (oid: string, message: string) => void;
const commitListeners: Set<CommitListener> = new Set();

export function onCommit(listener: CommitListener): () => void {
  commitListeners.add(listener);
  return () => commitListeners.delete(listener);
}

function emitCommit(oid: string, message: string): void {
  for (const listener of commitListeners) {
    try {
      listener(oid, message);
    } catch (e) {
      console.warn("[git-store] commit listener threw:", e);
    }
  }
}

/** Convenience for the common write-and-commit pattern. Returns the
 *  new oid, or null if neither the write nor the commit produced a
 *  change (the file already matched). */
export async function writeAndCommit(
  path: string,
  content: string,
  message: string,
): Promise<string | null> {
  const changed = await writeFile(path, content);
  if (!changed) return null;
  return commit(message);
}

// ─── Reads ──────────────────────────────────────────────────────────────────

/** Git log filtered to commits that touched a single file. Returns
 *  newest-first. */
export async function logForPath(path: string, depth = 50): Promise<GitCommitMeta[]> {
  await initGitStore();
  const fs = getFs();
  try {
    const entries = await git.log({
      fs,
      dir: REPO_DIR,
      filepath: path,
      depth,
      follow: false,
    });
    return entries.map((e) => ({
      oid: e.oid,
      message: e.commit.message.trim(),
      author: { name: e.commit.author.name, email: e.commit.author.email },
      timestamp: e.commit.author.timestamp * 1000,
    }));
  } catch (e) {
    // The first commit on an empty repo throws "Could not find HEAD"
    // until something has been committed. Treat as "no history yet".
    if (isNoCommits(e)) return [];
    throw e;
  }
}

/** List directory entries (filenames only, no recursion). Returns
 *  an empty array when the directory doesn't exist — callers don't
 *  need to disambiguate "empty" from "missing". */
export async function listDir(path: string): Promise<string[]> {
  await initGitStore();
  const fs = getFs();
  try {
    return await fs.promises.readdir(absPath(path));
  } catch (e) {
    if (isEnoent(e)) return [];
    throw e;
  }
}

/** Walk the whole working tree under data/ and return every text file
 *  with its current contents. Skips the .git directory and anything
 *  outside data/. Returns paths relative to the repo root (no leading
 *  slash). The GitHub backup (Phase 3) uses this to build a complete
 *  snapshot. */
export async function walkDataTree(): Promise<Array<{ path: string; content: string }>> {
  await initGitStore();
  const fs = getFs();
  const results: Array<{ path: string; content: string }> = [];

  async function walk(relDir: string): Promise<void> {
    const abs = absPath(relDir);
    let entries: string[];
    try {
      entries = await fs.promises.readdir(abs);
    } catch (e) {
      if (isEnoent(e)) return;
      throw e;
    }
    for (const name of entries) {
      const childRel = relDir === "" ? name : `${relDir}/${name}`;
      const childAbs = absPath(childRel);
      const stat = await fs.promises.stat(childAbs);
      if (stat.type === "dir" || stat.isDirectory?.()) {
        await walk(childRel);
      } else {
        const content = (await fs.promises.readFile(childAbs, "utf8")) as string;
        results.push({ path: childRel, content });
      }
    }
  }

  await walk("data");
  return results;
}

/** Current HEAD oid, or null when the repo has no commits yet. */
export async function headOid(): Promise<string | null> {
  await initGitStore();
  const fs = getFs();
  try {
    return await git.resolveRef({ fs, dir: REPO_DIR, ref: "HEAD" });
  } catch (e) {
    if (isNoCommits(e)) return null;
    throw e;
  }
}

/** Read a single file's contents at a specific commit. Returns null
 *  when the file didn't exist at that commit. */
export async function readFileAt(oid: string, path: string): Promise<string | null> {
  await initGitStore();
  const fs = getFs();
  try {
    const { blob } = await git.readBlob({
      fs,
      dir: REPO_DIR,
      oid,
      filepath: path,
    });
    return new TextDecoder().decode(blob);
  } catch (e) {
    if (isNotFound(e)) return null;
    throw e;
  }
}

// ─── Debug surface ──────────────────────────────────────────────────────────
//
// Exposed on window for in-browser smoke testing. Wired by the dev
// bootstrap (main.tsx); see git-store/debug.ts.

export const _internal = { getFs, REPO_DIR, FS_NAME };

// ─── Helpers ────────────────────────────────────────────────────────────────

async function hasStagedChanges(fs: FS): Promise<boolean> {
  // statusMatrix returns [filepath, head, workdir, stage]. A staged
  // change exists whenever head !== stage for any entry.
  const rows = await git.statusMatrix({ fs, dir: REPO_DIR });
  return rows.some(([, head, , stage]) => head !== stage);
}

async function mkdirP(abs: string): Promise<void> {
  const fs = getFs();
  if (abs === "/" || abs === "") return;
  const parts = abs.split("/").filter(Boolean);
  let cur = "";
  for (const p of parts) {
    cur += "/" + p;
    try {
      await fs.promises.mkdir(cur);
    } catch (e) {
      if (!isEexist(e)) throw e;
    }
  }
}

function absPath(path: string): string {
  const clean = path.replace(/^\/+/, "");
  return clean === "" ? REPO_DIR : `${REPO_DIR}/${clean}`;
}

function dirname(path: string): string {
  const idx = path.lastIndexOf("/");
  return idx < 0 ? "" : path.slice(0, idx);
}

function isEexist(e: unknown): boolean {
  return /EEXIST|already exists/i.test(errMsg(e));
}

function isEnoent(e: unknown): boolean {
  return /ENOENT|no such file/i.test(errMsg(e));
}

function isNoCommits(e: unknown): boolean {
  return /could not find HEAD|HEAD not found|resolveRef/i.test(errMsg(e));
}

function isNotFound(e: unknown): boolean {
  return /NotFoundError|not found|ENOENT/i.test(errMsg(e));
}

function errMsg(e: unknown): string {
  if (!e) return "";
  if (e instanceof Error) return `${e.name}: ${e.message}`;
  return String(e);
}
